/**
 * SA-717 (email triage spec T24): the metrics panel's numbers and words.
 * Pure. A rate is computed here from the counts, in whole numbers, so it
 * never rounds up (SA-407): the server's `agreement_rate` float is not
 * shown. An outcome code this page does not know reads "other".
 */
import type { TriageMetrics } from '@/services/mailboxes'

export interface KindRow {
  kind: string
  label: string
  waiting: number
  auto: number
  approved: number
  changed: number
  /** Where the changed ones went ("Internal 1, Automated 2"), or ''. */
  changedTo: string
  agreement: string
}

/** Approved and untouched (auto) rows agree; changed rows do not. */
export function agreementPercent(approved: number, auto: number, changed: number): number | null {
  const decided = approved + auto + changed
  return decided > 0 ? Math.floor(((approved + auto) * 100) / decided) : null
}

const label = (metrics: TriageMetrics, kind: string) => metrics.labels[kind] ?? kind

const tally = (metrics: TriageMetrics, counts: Record<string, number>) =>
  Object.entries(counts).map(([kind, n]) => `${label(metrics, kind)} ${n}`).join(', ')

export function kindRows(metrics: TriageMetrics): KindRow[] {
  return Object.entries(metrics.kinds).map(([kind, row]) => {
    const changed = Object.values(row.changed_to).reduce((sum, n) => sum + n, 0)
    const percent = agreementPercent(row.approved, row.auto, changed)
    return {
      kind, label: row.label, waiting: row.proposed, auto: row.auto, approved: row.approved, changed,
      changedTo: tally(metrics, row.changed_to),
      agreement: percent === null ? '—' : `${percent}%`,
    }
  })
}

/** SA-1421: Waiting is a state, so it counts whatever its age (it equals the
 *  mailbox row's count); the other figures cover the chosen window. */
export function waitingNote(days: number): string {
  return `Waiting counts every message still waiting, whatever its age; the other figures cover the last ${days} days.`
}

export function unknownLine(metrics: TriageMetrics): string | null {
  const given = tally(metrics, metrics.unknown.resolved_to)
  const parts = [
    metrics.unknown.waiting > 0 ? `${metrics.unknown.waiting} waiting for a kind` : null,
    given ? `given a kind by a person: ${given}` : null,
  ].filter(Boolean)
  return parts.length > 0 ? `No proposal: ${parts.join('; ')}.` : null
}

const BUCKET_WORDS: Record<string, string> = {
  lt_60: 'under 60%', '60_75': '60 to 75%', '75_90': '75 to 90%', gte_90: '90% and over',
}

export function calibrationLine(metrics: TriageMetrics): string {
  const parts = metrics.calibration.map((cell) => `${BUCKET_WORDS[cell.bucket] ?? cell.bucket}: ${cell.agreed} of ${cell.n}`)
  return `Model proposals people agreed with: ${parts.join('; ')}.`
}

// Sentence twin: components/inbox/triageText.ts's OUTCOME_WORDS words the same
// codes for one message ("did not finish"); this one words a count ("2 did not
// finish"). Different grammar, so not shared: move both together.
const OUTCOME_WORDS: Record<string, string> = {
  done: 'done', pending: 'waiting on a follow-up', skipped: 'not done', failed: 'did not finish', running: 'running',
  rejected: 'closed as not an enquiry',
}

export function outcomeLines(metrics: TriageMetrics): string[] {
  return Object.entries(metrics.handler_outcomes).map(([kind, counts]) => {
    const parts = Object.entries(counts).map(([outcome, n]) => `${n} ${OUTCOME_WORDS[outcome] ?? 'other'}`)
    return `${label(metrics, kind)}: ${parts.join(', ')}.`
  })
}

export function isEmpty(metrics: TriageMetrics): boolean {
  return Object.keys(metrics.kinds).length === 0
    && metrics.unknown.waiting === 0
    && Object.keys(metrics.unknown.resolved_to).length === 0
    && Object.keys(metrics.handler_outcomes).length === 0
}
